/**
 * Page-local URL params for InHouseClientsPage's two list-affecting pieces of state that were NOT
 * surviving back-navigation or a refresh: the tax-year selector and the status filter. Third
 * adoption of the pattern #78 established on TaxReturnsPage (useTaxReturnsScopeParams.ts), after
 * #97's second adoption on ClientOptOutsPage.
 *
 * `selectedClients` (the bulk-outreach checkbox selection set) is deliberately OUT of scope here,
 * same call as #78/#97 made for row selections elsewhere: it's a transient action set tied to one
 * intended bulk operation, not a view configuration, and stale selections surviving a Back
 * navigation would be the wrong default, not a missing feature.
 *
 * Conventions (mirroring useTaxReturnsScopeParams.ts / useClientOptOutsScopeParams.ts):
 * - `taxYear=<year>` is present only when it differs from the page's own default
 *   (currentYear - 1); an unparseable value falls back to that default, never NaN.
 * - `status=<value>` is present only when set to a non-default value; an unrecognised value falls
 *   back to 'all' rather than leaking a bad value into state.
 * - Mutations replace history (never push), matching the shared codec.
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

const currentYear = new Date().getFullYear();
const defaultTaxYear = currentYear - 1;

export type InHouseClientsFilterStatus = 'all' | 'missing' | 'setup';
const VALID_STATUSES: readonly InHouseClientsFilterStatus[] = ['all', 'missing', 'setup'];

export interface InHouseClientsScopeParams {
  taxYear: number;
  filterStatus: InHouseClientsFilterStatus;
  setTaxYear: (year: number) => void;
  setFilterStatus: (status: InHouseClientsFilterStatus) => void;
}

export function useInHouseClientsScopeParams(): InHouseClientsScopeParams {
  const [searchParams, setSearchParams] = useSearchParams();

  const taxYear = useMemo(() => {
    const raw = searchParams.get('taxYear');
    if (!raw) return defaultTaxYear;
    const parsed = Number(raw);
    return Number.isInteger(parsed) ? parsed : defaultTaxYear;
  }, [searchParams]);

  const filterStatus = useMemo<InHouseClientsFilterStatus>(() => {
    const raw = searchParams.get('status');
    return raw && (VALID_STATUSES as string[]).includes(raw)
      ? (raw as InHouseClientsFilterStatus)
      : 'all';
  }, [searchParams]);

  /** Replace history (never push), via the functional form — safe against a stale closure. */
  const update = useCallback(
    (mutate: (next: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setTaxYear = useCallback(
    (next: number) => {
      update((params) => {
        if (next === defaultTaxYear) params.delete('taxYear');
        else params.set('taxYear', String(next));
      });
    },
    [update]
  );

  const setFilterStatus = useCallback(
    (next: InHouseClientsFilterStatus) => {
      update((params) => {
        if (next === 'all') params.delete('status');
        else params.set('status', next);
      });
    },
    [update]
  );

  return { taxYear, filterStatus, setTaxYear, setFilterStatus };
}
